import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import SearchTileRowOverlineLocation from 'components/SearchKit/SearchTiles/shared/SearchTileRowOverlineLocation'
import Group from 'components/utils/Group'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import OfferUrlContext from 'contexts/OfferUrlContext'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import NumberRating from 'components/Common/NumberRating'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferRating from 'hooks/Offers/useOfferRating'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useAccommodationMapSearchPath from 'hooks/Search/useAccommodationMapSearchPath'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import useSearchTargetName from 'hooks/Search/useSearchTargetName'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { searchTileContentSpacingStyles } from '../searchTileLayoutStyles'
import _SearchTileBase, { _searchTileContainerQueryUp } from '../_SearchTileBase'
import _SearchTileHero from '../_SearchTileHero'
import SearchTileRowTitle from '../shared/SearchTileRowTitle'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getAccommodationSearchTileView } from './accommodationSearchTileView'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import BedbankSearchTileFeatures from './BedbankSearchTileFeatures'
import ExperimentalBedbankSearchTileFeatures from './ExperimentalBedbankSearchTileFeatures'
import BedbankLTSSearchTileFeatures from './BedbankLTSSearchTileFeatures'
import ExperimentalBedbankLTSSearchTileFeatures from './ExperimentalBedbankLTSSearchTileFeatures'
import BedbankSearchTilePriceStack from './BedbankSearchTilePriceStack'
import BedbankSearchTileUnavailabilityBanner from './BedbankSearchTileUnavailabilityBanner'
import useGroupedHotelSearchTileSecondaryOffer from './useGroupedHotelSearchTileSecondaryOffer'
import {
  GroupedHotelSearchTileOptionCard,
  GroupedHotelSearchTileOptionCards,
} from './GroupedHotelSearchTileOptionCards'
import GroupedHotelSearchTileSecondaryOptionCard from './GroupedHotelSearchTileSecondaryOptionCard'
import useMetaSearchDistance from './useMetaSearchDistance'
import useAdditionalProductImpressionRegistration from './useAdditionalProductImpressionRegistration'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import noop from 'lib/function/noop'
import { getAccommodationSearchPrimaryAction } from 'lib/search/accommodationSearchPresentation'

const MAX_ALTERNATIVE_PRIMARY_AMENITIES = 3

const Content = styled.div<{ $density: SearchTileDensity }>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${searchTileContentSpacingStyles}
`

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  align-items: start;
`

const RatingContainer = styled.div`
  display: none;

  ${_searchTileContainerQueryUp.tablet} {
    display: block;
  }
`

const Extension = styled.div`
  &:empty {
    display: none;
  }
`

const PrimaryPricePoint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  productClick?: (offer: App.AnyOffer) => void
  onAdditionalProductImpressionChange?: (offer?: App.AnyOffer) => void
  showDescription?: boolean
  position?: number
  eagerLoadFirstImage?: boolean
  density?: SearchTileDensity
  treatment?: SearchTileTreatment
}

function GroupedBedbankSearchTile({
  offer,
  filters,
  productClick = noop,
  onAdditionalProductImpressionChange = noop,
  showDescription = false,
  position,
  eagerLoadFirstImage,
  density = 'default',
  treatment = 'default',
}: Props) {
  const tileView = getAccommodationSearchTileView(density)
  const intl = useIntl()
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const mapSearchPath = useAccommodationMapSearchPath()
  const mapUrl = useMapSearchOfferUrl(false, offer, filters, mapSearchPath)
  const distanceFromSearchTarget = offerMetaData?.distance
  const searchTargetTitle = useSearchTargetName(filters)
  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights
  const promotions = useBedbankRoomRatePromotions(offer.promotions, filters?.checkIn, filters?.checkOut, isAnytimeSearch)
  const hasPromotions = !!Object.values(promotions).length
  const shouldUseSearchPrices = getShouldUseSearchPrices(filters)
  const shouldForceFetchRate = !!(filters?.checkIn && filters?.checkOut)
  const { offerPrice, fetchingRates, shownRate } = useBedbankOfferSearchPricing(offer, filters, undefined, shouldForceFetchRate)
  const hasDates = !!(filters?.checkIn && filters.checkOut)
  const isSoldOut = shouldUseSearchPrices ? !offerMetaData?.available : (!shownRate && hasDates && !fetchingRates)
  const hidePricing = useHideAnytimePrices(offer.type, offerMetaData?.hidePricing, offer.productType, filters)
  const showPricingBlock = !isSoldOut && (hidePricing || isDisplayablePrice(offerPrice))
  const rating = useOfferRating(offer, offerMetaData, fullPath.includes('metasearch'))
  const locations = useMemo<Array<string>>(() => {
    if (offer.property.locationTitleOverride) return [offer.property.locationTitleOverride]

    return [
      offer.property.address.city && offer.property.address.countryName
        ? intl.formatMessage(
            {
              id: 'ru9EIpXJE-YrgBlTMiJT',
              defaultMessage: '{city}, {country}',
              description: 'Bedbank search tile city and country separated by comma',
            },
            { city: offer.property.address.city, country: offer.property.address.countryName },
          )
        : (offer.property.address.city || offer.property.address.countryName || ''),
    ]
  }, [offer, intl])
  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer.property.timezone])
  const primaryOfferUrl = useContext(OfferUrlContext)
  const {
    secondaryOffer,
    secondaryOfferMetaData,
    fetching: fetchingSecondaryOffer,
  } = useGroupedHotelSearchTileSecondaryOffer(offer.id, filters)

  useAdditionalProductImpressionRegistration(onAdditionalProductImpressionChange, secondaryOffer)

  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const primaryAction = getAccommodationSearchPrimaryAction({
    offerType: offer.type,
    propertyType: offer.propertyType,
    hidePrice: hidePricing,
    isAnytimeSearch,
    tileKind: 'grouped',
    canSelectDates: !!onAnyHotelSelectDatesPrompt,
  })
  const shouldForceDateSelectionOnClick = primaryAction === 'select-dates'

  const hasSecondaryOffer = !!secondaryOffer || fetchingSecondaryOffer
  const offerLinkTarget = isOpenNewTabOfferClickEnabled ? '_blank' : undefined
  const handleOptionCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])
  const handlePrimaryOptionCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()

    if (shouldForceDateSelectionOnClick && onAnyHotelSelectDatesPrompt) {
      event.preventDefault()
      onAnyHotelSelectDatesPrompt(offer)
      productClick?.(offer)
      return
    }

    productClick?.(offer)
  }, [offer, productClick, shouldForceDateSelectionOnClick, onAnyHotelSelectDatesPrompt])
  const metaSearchDistanceToMainOffer = useMetaSearchDistance({ offer, filters })

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        offerId: offer.id,
        availability: false,
        roomRateId: undefined,
        extendedAvailability: offerMetaData?.extendedAvailability,
        price: undefined,
        memberPrice: undefined,
        duration: undefined,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  const primaryTitle = intl.formatMessage({
    id: '9jH0vVPiS_FpfyNqvK2O',
    defaultMessage: 'Available for your dates',
    description: 'Title for the available primary option card in an alternative grouped hotel search tile',
  })
  // Label must match what clicking the card actually does.
  const primaryActionLabel = shouldForceDateSelectionOnClick
    ? intl.formatMessage({
        id: 'qBTPc0sN1iCq7uv1ODCV',
        defaultMessage: 'Select dates',
        description: 'Button text prompting user to select dates on bedbank search tile',
      })
    : intl.formatMessage({
        id: 'wgbxIJoadg0wltSWEp8d',
        defaultMessage: 'View offer',
        description: 'Primary option card CTA label in the grouped hotel search result tile',
      })
  const primaryFeatures = (
    <>
      {!hasPromotions && (
        <>
          {!noGoogleAffiliations && <BedbankSearchTileFeatures
            offer={offer}
            filters={filters}
            truncateAfter={tileView.bedbankMaxInclusions}
            maxAmenitiesToShow={MAX_ALTERNATIVE_PRIMARY_AMENITIES}
            hideLuxPlusPricingSheet={tileView.hideBedbankLuxPlusPricing}
            hideRoomBreakdown
          />}
          {noGoogleAffiliations && <ExperimentalBedbankSearchTileFeatures
            offer={offer}
            filters={filters}
            maxInclusionsToShow={tileView.bedbankMaxInclusions}
            maxAmenitiesToShow={MAX_ALTERNATIVE_PRIMARY_AMENITIES}
            hideLuxPlusPricingLabel={tileView.hideBedbankLuxPlusPricing}
            hideRoomBreakdown
          />}
        </>
      )}
      {hasPromotions && (
        <>
          {!noGoogleAffiliations && <BedbankLTSSearchTileFeatures
            offer={offer}
            filters={filters}
            truncateAfter={tileView.bedbankMaxInclusions}
          />}
          {noGoogleAffiliations && <ExperimentalBedbankLTSSearchTileFeatures
            offer={offer}
            filters={filters}
            maxInclusionsToShow={tileView.bedbankMaxInclusions}
          />}
        </>
      )}
    </>
  )
  const primaryPricePoint = showPricingBlock && <PrimaryPricePoint>
    <BedbankSearchTilePriceStack
      offer={offer}
      filters={filters}
      hidePrice={hidePricing}
      hideIncludesTaxesAndFees={tileView.hideIncludesTaxesAndFees}
    />
  </PrimaryPricePoint>

  return <_SearchTileBase
    density={density}
    treatment={treatment}
    data-product={offer.productType}
    data-offer={offer.type}
    state="default"
    banner={showDescription && filters ? (
      <OfferDescriptionContainer
        filters={filters}
        offerId={offer.id}
        position={position}
      />
    ) : undefined}
  >
    <_SearchTileHero
      eagerLoadFirstImage={eagerLoadFirstImage}
      images={offer.images}
      productLabel={<LabelGroup>
        {hasPromotions && <ProductTypeLabel productType={offer.productType} />}
        {metaSearchDistanceToMainOffer && <PromotionLabel>{metaSearchDistanceToMainOffer}</PromotionLabel>}
      </LabelGroup>}
    />
    <Content $density={density}>
      <Header>
        <Group direction="vertical" gap={4}>
          {tileView.showLocationOverline && <SearchTileRowOverlineLocation
            locations={locations}
            mapUrl={mapUrl}
            distance={distanceFromSearchTarget}
            searchTargetLabel={searchTargetTitle}
            nearbyPois={offerMetaData?.nearbyPois}
            propertyId={offer.property?.id}
            showDistanceLabels={tileView.showLocationDistanceLabels}
          />}
          <SearchTileRowTitle>{offer.name}</SearchTileRowTitle>
          <ContentTagLabels offerId={offer.id} page="search" />
          {tileView.showCancellationPolicy && (shownRate || cancellationPolicy) && <BedbankCancellationPolicy
            rate={shownRate ?? cancellationPolicy}
            showNonCancellation={false}
          />}
        </Group>
        {rating && <RatingContainer>
          <NumberRating
            rating={rating.score}
            total={rating.reviewsTotal}
            variant="small"
            numberPosition="end"
          />
        </RatingContainer>}
      </Header>
      <GroupedHotelSearchTileOptionCards hasSecondaryOffer={hasSecondaryOffer}>
        <GroupedHotelSearchTileOptionCard
          title={primaryTitle}
          features={primaryFeatures}
          pricePoint={primaryPricePoint}
          actionLabel={primaryActionLabel}
          to={primaryOfferUrl}
          target={offerLinkTarget}
          onClick={handlePrimaryOptionCardClick}
          isPrimary
        />
        <GroupedHotelSearchTileSecondaryOptionCard
          filters={filters}
          groupedItemType={offerMetaData?.groupedItemType}
          bundledOfferId={offerMetaData?.bundledOfferId}
          secondaryOffer={secondaryOffer}
          secondaryOfferMetaData={secondaryOfferMetaData}
          fetchingSecondaryOffer={fetchingSecondaryOffer}
          noGoogleAffiliations={noGoogleAffiliations}
          maxTotalInclusions={tileView.groupedHotelMaxTotalInclusions}
          showSecondaryLuxPlusPromos={tileView.showSecondaryLuxPlusPromos}
          hidePrice={hidePricing}
          showPricePoint={!isSoldOut}
          hideIncludesTaxesAndFees={tileView.hideIncludesTaxesAndFees}
          useCompactPricing={tileView.useCompactPricing}
          onClick={handleOptionCardClick}
          productClick={productClick}
        />
      </GroupedHotelSearchTileOptionCards>
      <Extension>
        {isSoldOut && <BedbankSearchTileUnavailabilityBanner
          offer={offer}
          unavailableReason={offerMetaData?.unavailableReason}
          filters={filters}
        />}
      </Extension>
    </Content>
  </_SearchTileBase>
}

export default GroupedBedbankSearchTile
